// Run standup — full-screen walkthrough (design 2026-08-05 + project rail 2026-08-06).
// Steps through people in random order along a Jira-derived + pinned project rail:
// Yesterday → projects → Unassigned. Checklist-first cards; intent is derived.
// Park queue + receipt unchanged. Writes Focus + Activity(standup) on commit only.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalStyles } from "@mui/material";
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/700.css";
import "@fontsource/dm-mono/400.css";
import "@fontsource/dm-mono/500.css";
import {
  getStandupBoard,
  putStandupFocuses,
  setFocusDone,
  getStandupRun,
  putStandupRunPins,
  addStandupNote,
  type BoardMember,
  type Focus,
  type RunRail,
  type RunTab,
  type YesterdayRow,
} from "../../lib/api";
import {
  createProjectAction,
  createProjectActivity,
  listProjectsPublic,
  isOpenProjectStatus,
  type Project,
} from "../../lib/brain-api";
import { useSpeechInput } from "../../agent/useSpeechInput";
import { AgentProvider, useAgent } from "../../agent/AgentProvider";
import { AgentWidget } from "../../agent/AgentWidget";
import { CorrespondenceProvider } from "../../correspondence/CorrespondenceProvider";
import { brainTokens } from "../../design/brainTheme";
import { PersonAvatar } from "../../mui/PersonAvatar";
import { RailTabs } from "./run/RailTabs";
import { ProjectCard } from "./run/ProjectCard";
import {
  commitmentsForProject,
  commitmentsForUnassigned,
  deriveIntent,
  type DerivedIntent,
} from "./run/buildRail";

const bt = brainTokens.color;

// ---------------------------------------------------------------------------
// Types + run state
// ---------------------------------------------------------------------------

interface ParkedItem {
  id: string;
  text: string;
  person: string;
  projectId?: string;
  projectName?: string;
  atSec: number;
  resolution?: "action" | "note" | "dropped";
}

type Phase = "loading" | "run" | "park" | "receipt";

interface TickEntry { userId: string; date: string; done: boolean; wasDone: boolean }
interface PulledEntry { userId: string; text: string; projectId?: string; ticketKey?: string }

function shuffle<T>(a: T[]): T[] {
  const r = [...a];
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [r[i], r[j]] = [r[j]!, r[i]!];
  }
  return r;
}

function newId(): string {
  try { return crypto.randomUUID(); } catch { return `f-${Date.now()}-${Math.round(Math.random() * 1e6)}`; }
}

function initialsOf(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || "?";
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function mmss(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const kf = (userId: string, projectId: string) => `${userId}|${projectId}`;
const UNASSIGNED = "__unassigned__";

function projectTabsOf(rail: RunRail | undefined): Extract<RunTab, { kind: "project" }>[] {
  return (rail?.tabs ?? []).filter((t): t is Extract<RunTab, { kind: "project" }> => t.kind === "project");
}

// ---------------------------------------------------------------------------
// Page-scoped CSS (same GlobalStyles pattern as StandupMuiPage).
// ---------------------------------------------------------------------------
const RS_CSS = `
.rs-page{position:fixed;inset:0;z-index:1300;background:${bt.canvas};font-family:${brainTokens.font.sans};
  color:${bt.ink};display:flex;flex-direction:row;overflow:hidden;}
.rs-main{flex:1;min-width:0;display:flex;flex-direction:column;overflow:hidden;}
.rs-page *{box-sizing:border-box;}
.rs-page .agent-dock,.rs-page .agent-rail{position:static !important;top:0 !important;height:100vh !important;align-self:stretch;}
.rs-mono{font-family:${brainTokens.font.mono};}

.rs-strip{height:58px;flex:none;background:#fff;border-bottom:1px solid ${bt.border};display:flex;align-items:center;
  gap:18px;padding:0 20px;}
.rs-live{display:flex;align-items:center;gap:8px;font-family:${brainTokens.font.mono};font-size:11px;letter-spacing:.09em;color:${bt.ink2};}
.rs-dot{width:8px;height:8px;border-radius:50%;background:#cf4040;animation:rs-pulse 1.4s ease-in-out infinite;}
@keyframes rs-pulse{0%,100%{opacity:1;}50%{opacity:.35;}}
@media (prefers-reduced-motion: reduce){.rs-dot{animation:none;}}
.rs-timer{font-family:${brainTokens.font.mono};font-size:12px;color:${bt.muted};font-variant-numeric:tabular-nums;}
.rs-record{display:inline-flex;align-items:center;gap:8px;border-radius:999px;padding:6px 14px;cursor:pointer;
  font-family:${brainTokens.font.mono};font-size:11px;letter-spacing:.08em;border:1px solid ${bt.border};background:#fff;color:${bt.ink2};}
.rs-record .rs-dot{animation:none;}
.rs-record.on{background:#cf4040;border-color:#cf4040;color:#fff;}
.rs-record.on .rs-dot{background:#fff;animation:rs-pulse 1.2s ease-in-out infinite;}
.rs-people{flex:1;display:flex;align-items:center;justify-content:center;gap:8px;min-width:0;}
.rs-pc{width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex:none;
  font-size:11px;font-weight:700;background:#f7f9fa;color:${bt.faint};border:1px solid ${bt.border};}
.rs-pc.done{background:#e4f2f0;color:${bt.tealInk};border-color:transparent;}
.rs-pc.cur{background:${bt.teal};color:#fff;border:2px solid ${bt.teal};}
.rs-pc.absent{opacity:.38;text-decoration:line-through;}
.rs-count{font-family:${brainTokens.font.mono};font-size:11px;color:${bt.faint};white-space:nowrap;}
.rs-strip-right{display:flex;align-items:center;gap:14px;flex:none;}
.rs-parked-n{font-family:${brainTokens.font.mono};font-size:11px;letter-spacing:.08em;color:${bt.muted};}
.rs-esc{font-family:${brainTokens.font.mono};font-size:10.5px;color:${bt.placeholder};}

.rs-btn{display:inline-flex;align-items:center;gap:7px;border-radius:7px;padding:9px 16px;font-size:13.5px;font-weight:500;
  cursor:pointer;font-family:inherit;transition:background .12s;}
.rs-btn.pri{background:${bt.teal};color:#fff;border:none;}
.rs-btn.pri:hover{background:${bt.tealHover};}
.rs-btn.sec{background:#fff;color:${bt.ink2};border:1px solid ${bt.border};}
.rs-btn.sec:hover{background:#f5f6f7;}
.rs-btn.ghost{background:none;border:none;color:${bt.faint};}
.rs-btn.ghost:hover{color:${bt.ink2};}
.rs-btn.tint{background:${bt.tealTint};color:${bt.tealInk};border:1px solid #d3e7e5;}
.rs-btn.tint:hover{background:#dcefec;}
.rs-btn:disabled{opacity:.55;cursor:default;}

.rs-body{flex:1;overflow-y:auto;display:flex;justify-content:center;padding:34px 24px 60px;}
.rs-col{width:100%;max-width:860px;}
.rs-col.narrow{max-width:760px;}
.rs-col.receipt{max-width:820px;}

.rs-person{display:flex;align-items:center;gap:16px;margin-bottom:22px;}
.rs-person-info{flex:1;min-width:0;}
.rs-person-name{font-size:27px;font-weight:700;letter-spacing:-.01em;}
.rs-person-meta{font-size:13.5px;color:${bt.muted};margin-top:2px;}
.rs-person-actions{display:flex;gap:9px;flex:none;}

.rs-card{background:#fff;border:1px solid ${bt.border};border-radius:10px;padding:30px 34px;}
.rs-eyebrow{font-family:${brainTokens.font.mono};font-size:10.5px;letter-spacing:.11em;text-transform:uppercase;color:${bt.faint};}
.rs-h2{font-size:36px;font-weight:700;letter-spacing:-.015em;margin:6px 0 4px;}
.rs-sub{font-size:14.5px;color:${bt.muted};}

.rs-yrows{margin-top:18px;}
.rs-yrow{display:flex;align-items:center;gap:14px;padding:13px 0;border-top:1px solid ${bt.borderLight ?? "#f2f4f6"};}
.rs-ycheck{width:26px;height:26px;border-radius:7px;border:1.5px solid ${bt.dropBorder};background:#fff;flex:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;color:#fff;font-size:15px;transition:all .12s ease;}
.rs-ycheck.on{background:${bt.teal};border-color:${bt.teal};}
.rs-ymain{flex:1;min-width:0;}
.rs-ytext{font-size:17px;font-weight:500;}
.rs-ytext.done{color:#8a9099;text-decoration:line-through;}
.rs-yproj{font-family:${brainTokens.font.mono};font-size:11px;color:${bt.faint};margin-top:2px;}
.rs-carry{font-family:${brainTokens.font.mono};font-size:10.5px;background:${bt.dangerBg};color:${bt.dangerText};
  padding:2px 8px;border-radius:10px;flex:none;}
.rs-pull{flex:none;}
.rs-pulled{font-family:${brainTokens.font.mono};font-size:11px;background:${bt.tealTint};color:${bt.tealInk};
  padding:5px 11px;border-radius:999px;flex:none;}
.rs-hint{font-family:${brainTokens.font.mono};font-size:11.5px;color:${bt.placeholder};margin-top:20px;}

.rs-pills{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.rs-pill{display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:600;padding:4px 11px;border-radius:999px;}
.rs-pill .d{width:7px;height:7px;border-radius:50%;}
.rs-pill.green{background:${bt.successBg};color:${bt.successText};}
.rs-pill.green .d{background:${bt.successDot};}
.rs-pill.amber{background:${bt.warnBg};color:${bt.warnText};}
.rs-pill.amber .d{background:${bt.warnText};}
.rs-pill.red{background:${bt.dangerBg};color:${bt.dangerText};}
.rs-pill.red .d{background:${bt.dangerText};}
.rs-mpill{font-family:${brainTokens.font.mono};font-size:10.5px;letter-spacing:.06em;padding:3px 9px;border-radius:6px;
  background:${bt.panelBg2};color:${bt.muted};text-transform:uppercase;}
.rs-proj-n{margin-left:auto;font-family:${brainTokens.font.mono};font-size:11px;color:${bt.faint};}
.rs-proj-name{font-size:42px;font-weight:700;letter-spacing:-.02em;line-height:1.1;margin:14px 0 4px;}
.rs-proj-sub{font-size:14.5px;color:${bt.muted};margin-bottom:18px;}

.rs-ev{background:#eef7f6;border:1px solid #d3e7e5;border-radius:10px;padding:16px 18px;margin-bottom:18px;}
.rs-ev-hd{display:flex;align-items:center;gap:8px;margin-bottom:10px;}
.rs-ev-hd .rs-eyebrow{color:${bt.tealInk};}
.rs-ev-row{display:flex;align-items:center;gap:10px;padding:5px 0;font-size:16px;}
.rs-chip{font-family:${brainTokens.font.mono};font-size:11px;background:#eef6f6;border:1px solid #d6eaea;color:${bt.tealInk};
  padding:2px 8px;border-radius:5px;flex:none;}
.rs-ev-neutral{font-size:14.5px;color:${bt.muted};}

.rs-focusrow{display:flex;align-items:center;gap:10px;}
.rs-input{flex:1;border:1px solid ${bt.border};border-radius:8px;font-size:15px;padding:11px 13px;font-family:inherit;
  color:${bt.ink};outline:none;background:#fff;}
.rs-input:focus{border-color:${bt.teal};}
.rs-mic{width:40px;height:40px;border-radius:8px;background:${bt.teal};border:none;color:#fff;cursor:pointer;flex:none;
  display:flex;align-items:center;justify-content:center;font-size:16px;}
.rs-mic.on{background:#cf4040;}
.rs-recline{display:flex;align-items:center;gap:8px;font-family:${brainTokens.font.mono};font-size:11.5px;color:#cf4040;margin-top:9px;}

.rs-foot{display:flex;align-items:center;gap:14px;margin-top:22px;}
.rs-keys{flex:1;font-family:${brainTokens.font.mono};font-size:11.5px;color:${bt.placeholder};}

.rs-h1{font-size:34px;font-weight:700;letter-spacing:-.015em;margin:6px 0 18px;}
.rs-park-card{background:#fff;border:1px solid ${bt.border};border-radius:10px;padding:18px 20px;margin-bottom:12px;}
.rs-park-meta{display:flex;align-items:center;gap:9px;margin-bottom:8px;}
.rs-park-proj{font-size:12px;background:${bt.tealTint};color:${bt.tealInk};padding:3px 10px;border-radius:999px;}
.rs-park-sub{font-family:${brainTokens.font.mono};font-size:11px;color:${bt.faint};}
.rs-park-text{font-size:16.5px;margin-bottom:12px;}
.rs-park-actions{display:flex;gap:8px;}
.rs-resolved{font-family:${brainTokens.font.mono};font-size:12px;color:${bt.tealInk};background:${bt.tealTint};
  padding:5px 12px;border-radius:999px;display:inline-flex;}
.rs-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:18px;}
.rs-kpi{background:#fff;border:1px solid ${bt.border};border-radius:10px;padding:15px 17px;}
.rs-kpi .l{font-family:${brainTokens.font.mono};font-size:10px;letter-spacing:.1em;text-transform:uppercase;color:${bt.faint};}
.rs-kpi .v{font-size:30px;font-weight:700;margin-top:6px;}
.rs-rcard{background:#fff;border:1px solid ${bt.border};border-radius:10px;padding:20px 22px;margin-bottom:14px;}
.rs-rcard h3{font-size:15px;font-weight:600;margin:0 0 12px;}
.rs-rrow{display:flex;align-items:center;gap:10px;padding:7px 0;border-top:1px solid #f2f4f6;font-size:14px;}
.rs-rrow .who{font-family:${brainTokens.font.mono};font-size:11.5px;color:${bt.faint};flex:none;}
.rs-rrow .what{flex:1;min-width:0;}
.rs-ipill{font-family:${brainTokens.font.mono};font-size:10.5px;padding:2px 8px;border-radius:10px;flex:none;
  background:${bt.tealTint};color:${bt.tealInk};}
.rs-ipill.blocked{background:${bt.dangerBg};color:${bt.dangerText};}
.rs-ipill.parked,.rs-ipill.handover{background:${bt.panelBg2};color:${bt.muted};}
.rs-receipt-foot{font-size:13px;color:${bt.faint};margin:6px 2px 16px;}
.rs-empty{color:${bt.faint};font-size:13.5px;padding:8px 0;}
.rs-pin-list{margin-top:8px;border:1px solid ${bt.border};border-radius:8px;max-height:180px;overflow-y:auto;background:#fff;}
.rs-pin-row{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 12px;border-top:1px solid #f2f4f6;
  font-size:14px;cursor:pointer;}
.rs-pin-row:first-child{border-top:none;}
.rs-pin-row:hover{background:#f7f9fa;}
`;

export function RunStandupPage() {
  return (
    <AgentProvider>
      <CorrespondenceProvider>
        <div className="rs-page">
          <GlobalStyles styles={RS_CSS} />
          <RunStandupInner />
          <AgentWidget />
        </div>
      </CorrespondenceProvider>
    </AgentProvider>
  );
}

function RunStandupInner() {
  const navigate = useNavigate();
  const { setPageContext } = useAgent();

  const [phase, setPhase] = useState<Phase>("loading");
  const [boardDate, setBoardDate] = useState("");
  const [order, setOrder] = useState<BoardMember[]>([]);
  const [rails, setRails] = useState<Record<string, RunRail>>({});
  const [loadError, setLoadError] = useState<string | null>(null);

  const [personIdx, setPersonIdx] = useState(0);
  const [tabIdx, setTabIdx] = useState(0);
  const [absent, setAbsent] = useState<Record<string, boolean>>({});
  const [doneSet, setDoneSet] = useState<Record<string, boolean>>({});
  /** Per (userId|projectId) ticked issue keys. */
  const [tickedKeys, setTickedKeys] = useState<Record<string, string[]>>({});
  const [focusTexts, setFocusTexts] = useState<Record<string, string>>({});
  const [showAllIssues, setShowAllIssues] = useState<Record<string, boolean>>({});
  const [yTicks, setYTicks] = useState<Record<string, TickEntry>>({});
  const [pulled, setPulled] = useState<Record<string, PulledEntry>>({});
  const [parked, setParked] = useState<ParkedItem[]>([]);
  const [parkDraft, setParkDraft] = useState<string | null>(null);
  const [savedFocuses, setSavedFocuses] = useState<Record<string, Focus[]>>({});
  const [savedIntents, setSavedIntents] = useState<Record<string, DerivedIntent>>({});
  const [created, setCreated] = useState<{ chip: string; text: string }[]>([]);
  const [elapsed, setElapsed] = useState(0);

  const [openProjects, setOpenProjects] = useState<Project[]>([]);
  const [pinQuery, setPinQuery] = useState("");
  const [pinBusy, setPinBusy] = useState(false);

  const focusInputRef = useRef<HTMLInputElement>(null);
  const speechTargetRef = useRef<string | null>(null);
  const speech = useSpeechInput((text) => {
    const k = speechTargetRef.current;
    if (!k) return;
    setFocusTexts((f) => ({ ...f, [k]: f[k] ? `${f[k]} ${text}` : text }));
  });

  useEffect(() => {
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Load: board (roster) → shuffle → rails per person. No person-deltas / getBoard.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const board = await getStandupBoard();
        if (cancelled) return;
        const people = shuffle(board.members.filter((m) => m.kind !== "brain"));
        setBoardDate(board.date);
        setOrder(people);
        const results = await Promise.all(
          people.map((p) => getStandupRun(p.userId, board.date).catch(() => null)),
        );
        if (cancelled) return;
        const byUser: Record<string, RunRail> = {};
        people.forEach((p, i) => {
          const r = results[i];
          if (r) byUser[p.userId] = r;
        });
        setRails(byUser);
        void listProjectsPublic().then((ps) => {
          if (!cancelled) {
            setOpenProjects(ps.filter((p) => isOpenProjectStatus(p.status)));
          }
        }).catch(() => {});
        setPhase("run");
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const person = order[personIdx];
  const rail = person ? rails[person.userId] : undefined;
  const tabs = rail?.tabs ?? [];
  const tab = tabs[tabIdx];
  const projects = projectTabsOf(rail);
  const projectTab = tab?.kind === "project" ? tab : undefined;

  const remaining = order.filter((p, i) => i > personIdx && !absent[p.userId]).length;
  const doneCount = order.filter((p) => doneSet[p.userId]).length;

  const alreadyOnRail = useMemo(
    () => new Set(projects.map((p) => p.projectId)),
    [projects],
  );
  const pinMatches = useMemo(() => {
    const q = pinQuery.trim().toLowerCase();
    return openProjects
      .filter((p) => !alreadyOnRail.has(p.projectId))
      .filter((p) => !q || p.name.toLowerCase().includes(q) || p.projectId.includes(q))
      .slice(0, 12);
  }, [openProjects, alreadyOnRail, pinQuery]);

  useEffect(() => {
    if (!person || !tab) {
      speechTargetRef.current = null;
      return;
    }
    if (tab.kind === "project") speechTargetRef.current = kf(person.userId, tab.projectId);
    else if (tab.kind === "unassigned") speechTargetRef.current = kf(person.userId, UNASSIGNED);
    else speechTargetRef.current = null;
  }, [person, tab]);

  useEffect(() => {
    setPageContext({
      route: "/standup/run",
      entity: projectTab ? { type: "project", id: projectTab.projectId, name: projectTab.name } : undefined,
      snapshot: person
        ? {
            standupRun: true,
            person: person.displayName,
            step: tab?.kind ?? "rail",
            tabIdx,
            parked: parked.length,
          }
        : undefined,
    });
    return () => setPageContext(null);
  }, [setPageContext, person, projectTab, tab, tabIdx, parked.length]);

  // ── Persistence ─────────────────────────────────────────────────────────────

  const tickYesterday = (row: YesterdayRow, on: boolean) => {
    if (!person) return;
    setYTicks((t) => ({ ...t, [row.id]: { userId: person.userId, date: row.date, done: on, wasDone: row.done } }));
  };

  const toggleIssue = (userId: string, projectId: string, issueKey: string) => {
    const k = kf(userId, projectId);
    setTickedKeys((prev) => {
      const cur = new Set(prev[k] ?? []);
      if (cur.has(issueKey)) cur.delete(issueKey);
      else cur.add(issueKey);
      return { ...prev, [k]: [...cur] };
    });
  };

  const committedRef = useRef(false);
  const commitRun = useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    for (const [id, t] of Object.entries(yTicks)) {
      if (t.done !== t.wasDone) void setFocusDone(t.userId, t.date, id, t.done).catch(() => {});
    }
    const saved: Record<string, Focus[]> = {};
    const intents: Record<string, DerivedIntent> = {};
    for (const p of order) {
      const r = rails[p.userId];
      const actor = r?.actor?.trim() || p.userId;
      const additions: Focus[] = [];
      for (const pr of projectTabsOf(r)) {
        const k = kf(p.userId, pr.projectId);
        const keys = new Set(tickedKeys[k] ?? []);
        const ticked = pr.issues.filter((i) => keys.has(i.key));
        const typed = focusTexts[k] ?? "";
        intents[k] = deriveIntent(ticked, typed);
        const mapped = commitmentsForProject({
          projectId: pr.projectId,
          ticked,
          typedLine: typed,
          actor,
          boardDate,
        });
        additions.push(...mapped.focuses);
        for (const a of mapped.activities) {
          void createProjectActivity({
            projectId: a.projectId,
            text: a.text,
            type: "standup",
            date: a.nzDate,
            actor: a.actor,
            sourceRef: a.sourceRef,
          }).catch(() => {});
        }
      }
      const uk = kf(p.userId, UNASSIGNED);
      const untyped = focusTexts[uk] ?? "";
      const unmapped = commitmentsForUnassigned({ typedLine: untyped });
      additions.push(...unmapped.focuses);
      if (untyped.trim()) intents[uk] = "active";

      for (const pe of Object.values(pulled)) {
        if (pe.userId === p.userId) {
          additions.push({
            id: newId(),
            text: pe.text,
            done: false,
            projectId: pe.projectId,
            ticketKey: pe.ticketKey,
          });
        }
      }
      if (additions.length) {
        saved[p.userId] = additions;
        void putStandupFocuses(p.userId, boardDate, [...(p.focuses ?? []), ...additions]).catch(() => {});
      }
    }
    setSavedFocuses(saved);
    setSavedIntents(intents);
  }, [yTicks, order, rails, tickedKeys, focusTexts, pulled, boardDate]);

  const exitRun = useCallback(() => {
    commitRun();
    setPhase(parked.some((p) => !p.resolution) ? "park" : "receipt");
  }, [commitRun, parked]);

  const cancelRun = useCallback(() => {
    if (window.confirm("Cancel this standup? Nothing will be saved.")) navigate("/standup");
  }, [navigate]);

  const nextPerson = useCallback((fromIdx: number) => {
    let i = fromIdx + 1;
    while (i < order.length && absent[order[i]!.userId]) i++;
    if (i >= order.length) { exitRun(); return; }
    setPersonIdx(i);
    setTabIdx(0);
    setPinQuery("");
  }, [order, absent, exitRun]);

  const advance = useCallback(() => {
    if (!person) return;
    if (tabIdx < tabs.length - 1) { setTabIdx(tabIdx + 1); return; }
    setDoneSet((d) => ({ ...d, [person.userId]: true }));
    nextPerson(personIdx);
  }, [person, tabIdx, tabs.length, nextPerson, personIdx]);

  const back = useCallback(() => {
    if (tabIdx > 0) { setTabIdx(tabIdx - 1); return; }
    let i = personIdx - 1;
    while (i >= 0 && absent[order[i]!.userId]) i--;
    if (i >= 0) {
      const prev = order[i]!;
      const prevTabs = rails[prev.userId]?.tabs ?? [];
      setPersonIdx(i);
      setTabIdx(Math.max(0, prevTabs.length - 1));
    }
  }, [tabIdx, personIdx, order, absent, rails]);

  const markAbsent = useCallback(() => {
    if (!person) return;
    setAbsent((a) => ({ ...a, [person.userId]: true }));
    nextPerson(personIdx);
  }, [person, personIdx, nextPerson]);

  const parkThat = useCallback(() => setParkDraft(""), []);

  const commitPark = useCallback(() => {
    if (!person || !parkDraft?.trim()) { setParkDraft(null); return; }
    setParked((ps) => [...ps, {
      id: newId(), text: parkDraft.trim(), person: person.displayName,
      projectId: projectTab?.projectId, projectName: projectTab?.name, atSec: elapsed,
    }]);
    setParkDraft(null);
  }, [person, projectTab, parkDraft, elapsed]);

  const pinProject = useCallback(async (projectId: string) => {
    if (!person || pinBusy) return;
    setPinBusy(true);
    try {
      const existing = projectTabsOf(rails[person.userId])
        .filter((t) => t.pinned)
        .map((t) => t.projectId);
      const next = [...new Set([...existing, projectId])];
      await putStandupRunPins(person.userId, next);
      const refreshed = await getStandupRun(person.userId, boardDate);
      setRails((r) => ({ ...r, [person.userId]: refreshed }));
      setPinQuery("");
      const newIdx = refreshed.tabs.findIndex(
        (t) => t.kind === "project" && t.projectId === projectId,
      );
      if (newIdx >= 0) setTabIdx(newIdx);
    } catch {
      /* pin is best-effort mid-run */
    } finally {
      setPinBusy(false);
    }
  }, [person, pinBusy, rails, boardDate]);

  useEffect(() => {
    if (phase !== "run") return;
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) {
        if (e.key === "Enter" && parkDraft !== null) { e.preventDefault(); commitPark(); }
        if (e.key === "Escape") (el as HTMLElement).blur();
        return;
      }
      if (e.key === " " || e.key === "ArrowRight" || e.key === "Enter") { e.preventDefault(); advance(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); back(); }
      else if (e.key === "Escape") exitRun();
      else if (e.key.toLowerCase() === "p") parkThat();
      else if (e.key.toLowerCase() === "r") speech.toggle();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, advance, back, exitRun, parkThat, speech, parkDraft, commitPark]);

  const resolvePark = async (item: ParkedItem, how: "action" | "note" | "dropped") => {
    setParked((ps) => ps.map((p) => (p.id === item.id ? { ...p, resolution: how } : p)));
    try {
      if (how === "action") {
        const a = await createProjectAction({ text: item.text, projectId: item.projectId, kind: "action", status: "captured" });
        setCreated((c) => [...c, { chip: `ACTION${a.projectId ? ` · ${a.projectId}` : ""}`, text: item.text }]);
      } else if (how === "note") {
        await addStandupNote(item.text, "note");
        setCreated((c) => [...c, { chip: "NOTE", text: item.text }]);
      }
    } catch {
      setParked((ps) => ps.map((p) => (p.id === item.id ? { ...p, resolution: undefined } : p)));
    }
  };

  const strip = (
    <div className="rs-strip">
      <span className="rs-live"><span className="rs-dot" />STANDUP · LIVE</span>
      <span className="rs-timer">{mmss(elapsed)}</span>
      <button type="button" className={`rs-record${speech.listening ? " on" : ""}`} onClick={speech.toggle}
        disabled={!speech.supported}
        title={speech.error ?? (speech.supported ? "Record (R)" : "Speech recognition not supported in this browser")}>
        <span className="rs-dot" style={speech.listening ? undefined : { background: "#cf4040", animation: "none" }} />
        {speech.listening ? "RECORDING" : speech.transcribing ? "TRANSCRIBING…" : "RECORD"}
      </button>
      {speech.error && <span style={{ fontSize: 11.5, color: "#cf4040", maxWidth: 260 }}>{speech.error}</span>}
      <div className="rs-people">
        {order.map((p, i) => (
          <span key={p.userId}
            className={`rs-pc${doneSet[p.userId] ? " done" : ""}${i === personIdx && phase === "run" ? " cur" : ""}${absent[p.userId] ? " absent" : ""}`}
            title={p.displayName}>
            {initialsOf(p.displayName)}
          </span>
        ))}
        <span className="rs-count">{doneCount} done · {remaining + (phase === "run" ? 1 : 0)} to go</span>
      </div>
      <div className="rs-strip-right">
        <span className="rs-parked-n">PARKED {parked.length}</span>
        <button type="button" className="rs-btn ghost" onClick={cancelRun} title="Leave without saving anything">Cancel</button>
        <button type="button" className="rs-btn sec" onClick={exitRun}>End standup</button>
        <span className="rs-esc">ESC</span>
      </div>
    </div>
  );

  if (phase === "loading") {
    return (
      <div className="rs-main">
        <div className="rs-body"><div className="rs-col narrow" style={{ textAlign: "center", paddingTop: 120 }}>
          {loadError
            ? <><div className="rs-h2">Couldn't start standup</div><div className="rs-sub">{loadError}</div>
                <div style={{ marginTop: 18 }}><button type="button" className="rs-btn sec" onClick={() => navigate("/standup")}>Back to standup</button></div></>
            : <><span className="rs-eyebrow">STANDUP</span><div className="rs-h2">Shuffling the order…</div></>}
        </div></div>
      </div>
    );
  }

  if (phase === "park") {
    const unresolved = parked.filter((p) => !p.resolution).length;
    return (
      <div className="rs-main">
        {strip}
        <div className="rs-body"><div className="rs-col narrow">
          <span className="rs-eyebrow">STANDUP · PARK QUEUE</span>
          <h1 className="rs-h1">Parked moments</h1>
          {parked.length === 0 && <div className="rs-empty">Nothing was parked.</div>}
          {parked.map((item) => (
            <div key={item.id} className="rs-park-card">
              <div className="rs-park-meta">
                {item.projectName && <span className="rs-park-proj">{item.projectName}</span>}
                <span className="rs-park-sub">{item.person} · {mmss(item.atSec)}</span>
              </div>
              <div className="rs-park-text">{item.text}</div>
              {item.resolution
                ? <span className="rs-resolved">{item.resolution === "dropped" ? "dropped" : `✓ → ${item.resolution}`}</span>
                : (
                  <div className="rs-park-actions">
                    <button type="button" className="rs-btn tint" onClick={() => void resolvePark(item, "action")}>→ Action</button>
                    <button type="button" className="rs-btn tint" onClick={() => void resolvePark(item, "note")}>→ Standup note</button>
                    <button type="button" className="rs-btn sec" onClick={() => void resolvePark(item, "dropped")}>Drop</button>
                  </div>
                )}
            </div>
          ))}
          <div style={{ marginTop: 20 }}>
            <button type="button" className="rs-btn pri" onClick={() => setPhase("receipt")}>
              {unresolved > 0 ? `${unresolved} left — finish anyway` : "Finish → receipt"}
            </button>
          </div>
        </div></div>
      </div>
    );
  }

  if (phase === "receipt") {
    const focusRows: { who: string; project?: string; text: string; intent: DerivedIntent | "carried" }[] = [];
    for (const p of order) {
      for (const f of savedFocuses[p.userId] ?? []) {
        const projName = projectTabsOf(rails[p.userId]).find((x) => x.projectId === f.projectId)?.name;
        const k = f.projectId ? kf(p.userId, f.projectId) : kf(p.userId, UNASSIGNED);
        const intent: DerivedIntent | "carried" = savedIntents[k]
          ?? (f.issueKey ? "active" : "carried");
        focusRows.push({ who: firstName(p.displayName), project: projName, text: f.text, intent });
      }
    }
    const ticked = Object.values(yTicks).filter((t) => t.done && !t.wasDone).length;
    const parkedResolved = parked.filter((p) => p.resolution && p.resolution !== "dropped").length;
    return (
      <div className="rs-main">
        <div className="rs-body"><div className="rs-col receipt">
          <span className="rs-eyebrow rs-mono">STANDUP · {boardDate} · {mmss(elapsed)}</span>
          <h1 className="rs-h1">Standup receipt</h1>
          <div className="rs-receipt-foot">Focuses land on tomorrow's standup; activities are written best-effort per project.</div>
          <div className="rs-kpis">
            <div className="rs-kpi"><div className="l">Focuses set</div><div className="v">{focusRows.length}</div></div>
            <div className="rs-kpi"><div className="l">Yesterday cleared</div><div className="v">{ticked}</div></div>
            <div className="rs-kpi"><div className="l">Actions &amp; notes</div><div className="v">{created.length}</div></div>
            <div className="rs-kpi"><div className="l">Parked resolved</div><div className="v">{parkedResolved}/{parked.length}</div></div>
          </div>
          <div className="rs-rcard">
            <h3>Focuses set — show up tomorrow</h3>
            {focusRows.length === 0 && <div className="rs-empty">No focuses were set this run.</div>}
            {focusRows.map((r, i) => (
              <div key={i} className="rs-rrow">
                <span className="who">{r.who}{r.project ? ` · ${r.project}` : ""}</span>
                <span className="what">{r.text}</span>
                <span className={`rs-ipill ${r.intent}`}>{r.intent === "carried" ? "Carried" : r.intent}</span>
              </div>
            ))}
          </div>
          <div className="rs-rcard">
            <h3>Created &amp; resolved</h3>
            {created.length === 0 && <div className="rs-empty">Nothing created.</div>}
            {created.map((c, i) => (
              <div key={i} className="rs-rrow"><span className="rs-chip">{c.chip}</span><span className="what">{c.text}</span></div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
            <button type="button" className="rs-btn pri" onClick={() => navigate("/standup")}>Back to standup</button>
          </div>
        </div></div>
      </div>
    );
  }

  if (!person) {
    return (
      <div className="rs-main">{strip}
        <div className="rs-body"><div className="rs-col narrow" style={{ textAlign: "center", paddingTop: 100 }}>
          <div className="rs-h2">Nobody on the board</div>
          <button type="button" className="rs-btn sec" onClick={() => navigate("/standup")}>Back to standup</button>
        </div></div>
      </div>
    );
  }

  const activeCount = order.filter((p) => !absent[p.userId]).length;
  const personPos = order.slice(0, personIdx + 1).filter((p) => !absent[p.userId]).length;

  const personHeader = (
    <div className="rs-person">
      <PersonAvatar name={person.displayName} size={52} />
      <div className="rs-person-info">
        <div className="rs-person-name">{person.displayName}</div>
        <div className="rs-person-meta">
          Person {personPos} of {activeCount} · {projects.length} project{projects.length === 1 ? "" : "s"} on the rail
        </div>
      </div>
      <div className="rs-person-actions">
        <button type="button" className="rs-btn ghost" onClick={markAbsent}>Absent</button>
        <button type="button" className="rs-btn sec" onClick={() => nextPerson(personIdx)}>Skip person</button>
      </div>
    </div>
  );

  const parkBar = parkDraft !== null && (
    <div className="rs-card" style={{ marginTop: 14, padding: "16px 18px" }}>
      <div className="rs-eyebrow" style={{ marginBottom: 8 }}>PARK THAT</div>
      <div className="rs-focusrow">
        <input className="rs-input" autoFocus value={parkDraft} placeholder="Capture the moment — resolved in one pass at the end"
          onChange={(e) => setParkDraft(e.target.value)} />
        <button type="button" className="rs-btn pri" onClick={commitPark}>Park</button>
        <button type="button" className="rs-btn ghost" onClick={() => setParkDraft(null)}>Cancel</button>
      </div>
    </div>
  );

  const footer = (hint: string) => (
    <div className="rs-foot">
      <span className="rs-keys">{hint}</span>
      <button type="button" className="rs-btn sec" onClick={parkThat}>Park that</button>
      <button type="button" className="rs-btn pri" onClick={advance}>Next →</button>
    </div>
  );

  const railNav = tabs.length > 0 && (
    <RailTabs tabs={tabs} tabIdx={tabIdx} onChange={setTabIdx} />
  );

  // Fallback empty rail
  if (!tab) {
    return (
      <div className="rs-main">
        {strip}
        <div className="rs-body"><div className="rs-col">
          {personHeader}
          <div className="rs-card">
            <div className="rs-empty">No rail for this person — skip or mark absent.</div>
          </div>
          {footer("SPACE next person · P park · ESC end")}
        </div></div>
      </div>
    );
  }

  if (tab.kind === "yesterday") {
    const rows = tab.rows;
    return (
      <div className="rs-main">
        {strip}
        <div className="rs-body"><div className="rs-col">
          {personHeader}
          {railNav}
          <div className="rs-card">
            <span className="rs-eyebrow">YESTERDAY</span>
            <div className="rs-h2">Tick what got done</div>
            <div className="rs-sub">Yesterday's focuses — tick it off, or pull it back into today.</div>
            <div className="rs-yrows">
              {rows.length === 0 && <div className="rs-empty" style={{ paddingTop: 16 }}>No focuses from yesterday.</div>}
              {rows.map((row) => {
                const ticked = yTicks[row.id]?.done ?? row.done;
                return (
                  <div key={row.id} className="rs-yrow">
                    <button type="button" className={`rs-ycheck${ticked ? " on" : ""}`} aria-pressed={ticked}
                      onClick={() => tickYesterday(row, !ticked)}>{ticked ? "✓" : ""}</button>
                    <div className="rs-ymain">
                      <div className={`rs-ytext${ticked ? " done" : ""}`}>{row.text}</div>
                      {row.projectId && <div className="rs-yproj">{row.projectId}</div>}
                    </div>
                    {row.carriedDays > 1 && <span className="rs-carry">{row.carriedDays}d ago</span>}
                    {pulled[row.id]
                      ? <span className="rs-pulled">→ TODAY</span>
                      : !ticked && (
                        <button
                          type="button"
                          className="rs-btn sec rs-pull"
                          onClick={() => person && setPulled((p) => ({
                            ...p,
                            [row.id]: { userId: person.userId, text: row.text, projectId: row.projectId, ticketKey: row.ticketKey },
                          }))}
                        >
                          Pull into today
                        </button>
                      )}
                  </div>
                );
              })}
            </div>
            <div className="rs-hint rs-mono">SPACE — next tab on {firstName(person.displayName)}'s rail</div>
          </div>
          {parkBar}
          {footer("SPACE next · ← back · P park · R record · ESC end")}
        </div></div>
      </div>
    );
  }

  if (tab.kind === "project") {
    const k = kf(person.userId, tab.projectId);
    const keys = new Set(tickedKeys[k] ?? []);
    const projIndex = projects.findIndex((p) => p.projectId === tab.projectId) + 1;
    return (
      <div className="rs-main">
        {strip}
        <div className="rs-body"><div className="rs-col">
          {personHeader}
          {railNav}
          <ProjectCard
            tab={tab}
            index={Math.max(1, projIndex)}
            total={Math.max(1, projects.length)}
            tickedKeys={keys}
            typedLine={focusTexts[k] ?? ""}
            onToggleIssue={(issueKey) => toggleIssue(person.userId, tab.projectId, issueKey)}
            onTypedChange={(v) => setFocusTexts((f) => ({ ...f, [k]: v }))}
            showAll={!!showAllIssues[k]}
            onShowAll={() => setShowAllIssues((s) => ({ ...s, [k]: true }))}
            focusInputRef={focusInputRef}
            speechListening={speech.listening}
            speechSupported={speech.supported}
            onToggleSpeech={speech.toggle}
            speechError={speech.error}
          />
          {parkBar}
          {footer("SPACE next · ← back · P park · R record · ESC end")}
        </div></div>
      </div>
    );
  }

  // Unassigned
  const uk = kf(person.userId, UNASSIGNED);

  return (
    <div className="rs-main">
      {strip}
      <div className="rs-body"><div className="rs-col">
        {personHeader}
        {railNav}
        <div className="rs-card">
          <span className="rs-eyebrow">UNASSIGNED</span>
          <div className="rs-h2">No project yet</div>
          <div className="rs-sub">Carried focuses without a project — tick, pull, or type a new one. Pin a project to grow the rail.</div>

          <div className="rs-yrows">
            {tab.rows.length === 0 && <div className="rs-empty" style={{ paddingTop: 16 }}>Nothing unassigned.</div>}
            {tab.rows.map((row) => {
              const ticked = yTicks[row.id]?.done ?? row.done;
              return (
                <div key={row.id} className="rs-yrow">
                  <button type="button" className={`rs-ycheck${ticked ? " on" : ""}`} aria-pressed={ticked}
                    onClick={() => tickYesterday(row, !ticked)}>{ticked ? "✓" : ""}</button>
                  <div className="rs-ymain">
                    <div className={`rs-ytext${ticked ? " done" : ""}`}>{row.text}</div>
                  </div>
                  {row.carriedDays > 1 && <span className="rs-carry">{row.carriedDays}d ago</span>}
                  {pulled[row.id]
                    ? <span className="rs-pulled">→ TODAY</span>
                    : !ticked && (
                      <button
                        type="button"
                        className="rs-btn sec rs-pull"
                        onClick={() => person && setPulled((p) => ({
                          ...p,
                          [row.id]: { userId: person.userId, text: row.text },
                        }))}
                      >
                        Pull into today
                      </button>
                    )}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 18 }}>
            <span className="rs-eyebrow">NEW FOCUS (NO PROJECT)</span>
            <div className="rs-focusrow" style={{ marginTop: 6 }}>
              <input
                ref={focusInputRef}
                className="rs-input"
                value={focusTexts[uk] ?? ""}
                placeholder="Something that doesn't belong on a project yet"
                onChange={(e) => setFocusTexts((f) => ({ ...f, [uk]: e.target.value }))}
              />
              <button type="button" className={`rs-mic${speech.listening ? " on" : ""}`} onClick={speech.toggle}
                disabled={!speech.supported} aria-label="Dictate focus">
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="3" width="6" height="11" rx="3" />
                  <path d="M5 11a7 7 0 0 0 14 0" />
                  <path d="M12 18v3" />
                </svg>
              </button>
            </div>
          </div>

          <div style={{ marginTop: 22 }}>
            <span className="rs-eyebrow">PIN A PROJECT TO THIS RAIL</span>
            <div className="rs-focusrow" style={{ marginTop: 6 }}>
              <input
                className="rs-input"
                value={pinQuery}
                placeholder="Search open projects…"
                onChange={(e) => setPinQuery(e.target.value)}
              />
            </div>
            {pinQuery.trim() && (
              <div className="rs-pin-list">
                {pinMatches.length === 0 && <div className="rs-empty" style={{ padding: 12 }}>No matches.</div>}
                {pinMatches.map((p) => (
                  <div
                    key={p.projectId}
                    className="rs-pin-row"
                    onClick={() => void pinProject(p.projectId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter") void pinProject(p.projectId); }}
                  >
                    <span>{p.name}<span className="rs-yproj" style={{ display: "inline", marginLeft: 8 }}>{p.projectId}</span></span>
                    <button type="button" className="rs-btn tint" disabled={pinBusy}>Pin</button>
                  </div>
                ))}
              </div>
            )}
            <div className="rs-hint rs-mono" style={{ marginTop: 10 }}>
              Pinning updates your profile, then reloads this rail.
            </div>
          </div>
        </div>
        {parkBar}
        {footer("SPACE next person · ← back · P park · R record · ESC end")}
      </div></div>
    </div>
  );
}
